import { File, FolderOpen } from 'lucide-react';
import { C } from '../../theme.js';
import { Alert, Button, FieldLabel } from '../../components/Primitives.jsx';
import { SourceSelect } from '../shared/SourceSelect.jsx';
import { OutputNameField } from '../shared/OutputNameField.jsx';
import { Segmented } from '../StepInspector.jsx';

// Mirrors union_named_sources() in server/app/engine/steps/joins.py — keep
// in sync. Named-source mode only applies when self is empty.
function namedSourcesActive(c, selfCount) {
  if (selfCount > 0) return false;
  const on = (v) => v && v !== '__self__';
  return !!(on(c.left_source) || on(c.right_source));
}

export function UnionInspector({ step, onChange, onBrowseJoinFile, secondaryDatasets, sheets, pipeline, runMap, uploadedRows }) {
  const c = step.config || {};
  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });

  // Other dataset info
  const rightData = secondaryDatasets?.get(c.right);
  const rightCols = rightData?.columns || [];
  const selfRowCount = runMap?.get(step.id)?.inputCount ?? uploadedRows?.length ?? 0;
  const named = namedSourcesActive(c, selfRowCount);
  const sourceField = (label, key, hint) => (
    <div style={{ marginBottom: 10 }}>
      <FieldLabel hint={hint}>{label}</FieldLabel>
      <SourceSelect
        value={c[key]}
        onChange={(v) => u({ [key]: v })}
        secondaryDatasets={secondaryDatasets}
        sheets={sheets}
        pipeline={pipeline}
        currentStep={step}
        onBrowse={onBrowseJoinFile ? () => onBrowseJoinFile(step.id, (name) => u({ [key]: name })) : undefined}
        buildTargetName={pipeline?.inputFile?.buildTargetName}
      />
    </div>
  );

  return (
    <div>
      {sourceField('Left source (Optional)', 'left_source', 'Optional — used only when self is empty. Rows from this Source go first; leave as self to use the live pipeline rows')}
      {sourceField('Right source (Optional)', 'right_source', 'Optional — used only when self is empty. Rows from this Source are stacked below the left source')}
      {named && (
        <Alert kind="info" style={{ marginBottom: 10 }}>
          Self is empty, so this step stacks the left and right Sources directly. A Source that can&rsquo;t be found stops the run with an error.
        </Alert>
      )}
      {/* Other dataset card */}
      <FieldLabel>Other dataset</FieldLabel>
      <div style={{
        background: C.input, border: `1px solid ${C.border}`, borderRadius: 8,
        padding: '10px 12px', marginBottom: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'inline-flex', color: C.fg4 }}><File size={16} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              color: c.right ? C.fg1 : C.fg5,
              fontSize: '.82rem', fontWeight: 600,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{c.right || 'No file selected'}</div>
            {rightData && (
              <div style={{ color: C.fg5, fontSize: '.72rem', marginTop: 2 }}>
                {rightData.rows.length} rows · {rightCols.length} columns
              </div>
            )}
            {!rightData && c.right && (
              <div style={{ color: C.fg5, fontSize: '.72rem', marginTop: 2 }}>
                No data loaded
              </div>
            )}
          </div>
          <Button kind="ghost" size="sm" icon={<FolderOpen size={14} />} onClick={() => onBrowseJoinFile?.(step.id)}>
            Browse…
          </Button>
        </div>
      </div>
      <FieldLabel>Column alignment</FieldLabel>
      <Segmented value={c.align || 'name'} onChange={(v) => u({ align: v })}
        options={[
          { value: 'name', label: 'By column name' },
          { value: 'position', label: 'By position' },
        ]} />
      <FieldLabel>Mismatched columns</FieldLabel>
      <Segmented value={c.mismatch || 'fill'} onChange={(v) => u({ mismatch: v })}
        options={[
          { value: 'fill', label: 'Fill nulls' },
          { value: 'drop', label: 'Drop' },
          { value: 'halt', label: 'Halt' },
        ]} />
      <OutputNameField
        value={c.output_name}
        onChange={(v) => u({ output_name: v })}
        required={false}
        hint="Optional — also publish the stacked result under this name so later steps can pick it as a Source"
        placeholder="e.g., output_sap"
      />
      {named && (
        <Alert kind="info" style={{ marginTop: 6 }}>
          The stacked result is written to self — every step after this one sees it as the pipeline&rsquo;s live rows
          {c.output_name ? <> (and it&rsquo;s also published as &ldquo;{c.output_name}&rdquo;)</> : null}.
        </Alert>
      )}
    </div>
  );
}
